import type { TypeSubjectType } from '@/types/begateway/types.begateway.access';

export const CIDR_MIN_PREFIX = 8;

export const SUBJECT_VALUE_MAX: Record<TypeSubjectType, number> = { ip: 45, cidr: 18, device: 128, user: 128, service: 64, any: 1 };

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const IPV6 = /^[0-9a-fA-F:]{2,39}$/;
const CIDR_V4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}\/(3[0-2]|[12]?\d)$/;
const IDENTIFIER = /^[\x21-\x7e]{1,128}$/;
const SERVICE = /^[A-Za-z0-9_-]{1,64}$/;

export const isIpv4 = (value: string): boolean => {
    const match = IPV4.exec(value);
    return Boolean(match && match.slice(1).every((octet) => Number(octet) <= 255 && (octet === '0' || !octet.startsWith('0'))));
};

export const isIpAddress = (raw: string): boolean => {
    const value = raw.trim();
    if (!value || value.includes('/')) return false;
    if (isIpv4(value)) return true;
    return value.includes(':') && IPV6.test(value) && value.split('::').length <= 2;
};

export function cleanSubjectValue(type: TypeSubjectType, raw: string): string {
    switch (type) {
        case 'ip':
            return raw.replace(/[^0-9a-fA-F.:]/g, '').slice(0, SUBJECT_VALUE_MAX.ip);
        case 'cidr': {
            const [base = '', ...rest] = raw.replace(/[^0-9./]/g, '').split('/');
            const value = rest.length ? `${base.slice(0, 15)}/${rest.join('').replace(/\./g, '').slice(0, 2)}` : base.slice(0, 15);
            return value.slice(0, SUBJECT_VALUE_MAX.cidr);
        }
        case 'device':
        case 'user':
            return raw.replace(/[^\x21-\x7e]/g, '').slice(0, SUBJECT_VALUE_MAX[type]);
        case 'service':
            return raw.replace(/[^A-Za-z0-9_-]/g, '').slice(0, SUBJECT_VALUE_MAX.service);
        default:
            return '';
    }
}

export function subjectError(type: TypeSubjectType, raw: string): string | null {
    if (type === 'any') return null;
    const value = raw.trim();
    if (!value) return 'Indica il valore del soggetto';

    switch (type) {
        case 'ip':
            if (value.includes('/')) return 'Questa è una rete: scegli «Rete (CIDR)»';
            return isIpAddress(value) ? null : 'Indirizzo IP non valido, per esempio 203.0.113.7';
        case 'cidr':
            if (!CIDR_V4.test(value)) return 'Rete non valida: serve un CIDR IPv4 come 10.0.0.0/24';
            return Number(value.split('/')[1]) < CIDR_MIN_PREFIX
                ? `Una rete più larga di /${CIDR_MIN_PREFIX} vale come «Chiunque»: scegli quello, oppure una rete più stretta`
                : null;
        case 'service':
            return SERVICE.test(value) ? null : 'Il servizio si indica col nome o con l’identificativo';
        default:
            return IDENTIFIER.test(value) ? null : 'Identificativo non valido: niente spazi né caratteri di controllo';
    }
}
